/**
 * admin(Laravel) 인증 헬퍼.
 *
 * POST /api/admin/login 으로 로그인 → RS256 토큰과 auth_info(gnbs 메뉴권한 포함) 수신.
 * 토큰은 lmsApi/adminApi 공용 키("token")에 저장하여 하나의 토큰으로 두 백엔드를 호출한다.
 * auth_info(gnbs 등)는 admin 메뉴 렌더링·화면 접근 제어에 사용한다.
 */

import { lmsApi } from "./api";

const TOKEN_KEY = "token"; // api.ts 와 공유 (동일 토큰)
const AUTH_KEY = "admin_auth"; // gnbs 등 로그인 페이로드

export interface AdminGnbRoute {
  route: string;
  name?: string;
  auth?: number[];
  [k: string]: unknown;
}

export interface AdminGnb {
  group?: string;
  route?: string | AdminGnbRoute[];
  auth?: number[];
  [k: string]: unknown;
}

export interface AdminAuthInfo {
  AccountID: number;
  AccountTypeID: number;
  Account: string;
  Name?: string;
  AgencyID?: number;
  AcademyID?: number;
  gnbs?: AdminGnb[];
  [k: string]: unknown;
}

interface AdminLoginResponse {
  result: number;
  token_type?: string;
  access_token?: string;
  auth_info?: AdminAuthInfo;
  errorMsg?: string;
}

export interface AdminLoginResult {
  ok: boolean;
  errorMsg?: string;
  auth?: AdminAuthInfo;
}

export async function adminLogin(
  account: string,
  password: string,
): Promise<AdminLoginResult> {
  const data = await lmsApi.post<AdminLoginResponse>("/api/lms/admin-login", {
    account,
    password,
  });

  if (data.result === 1 && data.access_token) {
    const info = (data.auth_info ?? {}) as AdminAuthInfo;
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(AUTH_KEY, JSON.stringify(info));
    // AccountTypeID(숫자) → LMS accountType 문자열 (LMS 메뉴 권한 필터용)
    const typeMap: Record<number, string> = { 1: "root", 2: "agency", 3: "academy", 4: "teacher", 8: "book" };
    const type = typeMap[info.AccountTypeID as number] || "";
    localStorage.setItem("accountType", type);
    // 신원 정보(상단바 표시용)는 로그인마다 새 계정 값으로 '항상' 덮어쓴다.
    // (부분 세팅 시 이전 계정의 agencyName/academyName 등이 잔존 → 상단바에 이전 계정 정보가 뜨는 문제 방지)
    localStorage.setItem("userName", String(info.OwnerName ?? info.Name ?? info.Account ?? ""));
    localStorage.setItem("account", String(info.Account ?? ""));
    localStorage.setItem("agencyName", type === "agency" ? String(info.Name ?? "") : "");
    localStorage.setItem("academyName", type === "academy" ? String(info.Name ?? "") : "");
    localStorage.setItem("agencyID", String(info.AgencyID ?? info.ParentAgencyID ?? ""));
    localStorage.setItem("academyID", String(info.AcademyID ?? ""));
    return { ok: true, auth: info };
  }

  // 서버는 잘못된 자격증명에도 200 + result:0 을 반환한다
  return { ok: false, errorMsg: data.errorMsg || "로그인에 실패했습니다." };
}

/**
 * 로그인·기본 진입(`/`·404) 시 계정 타입별 첫 화면.
 * accountType 은 typeMap(1=root,2=agency,3=academy,4=teacher,8=book) 기준.
 */
export function landingPath(): string {
  switch (localStorage.getItem("accountType")) {
    case "academy":
      return "/learning-statistics"; // 원장 → 학생 통계
    case "root":
    case "agency":
      return "/admin/notice"; // 본사·지사 → 일반공지
    case "book":
      return "/admin/od-delivery"; // book(type 8) → 배송
    default:
      return "/admin/notice"; // 교사 등 그 외 → 일반공지(넓게 접근 가능)
  }
}

// 학습관리 화면에서 교사(teacher)는 소속 학원(academy)과 동일 범위로 취급한다.
// (백엔드도 root/agency 외 계정은 user.academyId 로 스코프하므로 교사=자기 학원.)
// 교재 부여하기·링키고 셋팅 등 교사 제외 화면에는 사용하지 않는다.
export function learningScopeAccountType(): string {
  const t = localStorage.getItem("accountType") ?? "";
  return t === "teacher" ? "academy" : t;
}

export function adminLogout(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(AUTH_KEY);
  localStorage.removeItem("accountType");
}

/** admin 앱(Laravel) base URL */
export function adminAppUrl(): string {
  return (import.meta.env.VITE_ADMIN_API_URL || "").replace(/\/$/, "");
}

/** 아직 LMS로 이관되지 않은 admin 화면을 기존 admin 앱에서 SSO 로 연다 (토큰→세션) */
export function openAdminScreen(route: string): void {
  const token = localStorage.getItem(TOKEN_KEY) || "";
  const to = route.startsWith("/") ? route : "/" + route;
  window.location.href =
    `${adminAppUrl()}/sso-login?token=${encodeURIComponent(token)}&to=${encodeURIComponent(to)}`;
}

export function getAdminAuth(): AdminAuthInfo | null {
  const raw = localStorage.getItem(AUTH_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AdminAuthInfo;
  } catch {
    return null;
  }
}

export function getAdminGnbs(): AdminGnb[] {
  return getAdminAuth()?.gnbs ?? [];
}

export function isAdminLoggedIn(): boolean {
  return !!localStorage.getItem(TOKEN_KEY) && !!getAdminAuth();
}
